import { Grid, makeStyles, Typography } from '@material-ui/core';
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ClientConfig } from '../../../clientConfig';
import { ClientConfigContext } from '../../clientConfigContext';
import { useBrand } from '../../hooks/useBrand';
import CustomerServiceCard from '../containers/CustomerServiceCard';
import PromptCard from '../containers/PromptCard';
import RedirectButtonCard from '../containers/RedirectButtonCard';
import TransitionCard from '../containers/TransitionCard';
import VerifyContactCustomerService from '../containers/VerifyContactCustomerService';
import { AuthenticatedRoutes } from '../routers';
import { ActivationCodeCard } from './activation/ActivationCodePage';
import { AutoInstantActivationAuthorizationCard } from './autoinstantactivation/AutoInstantActivationAuthorizationPage';
import { AutoInstantActivationLandingCard } from './autoinstantactivation/AutoInstantActivationLandingPage';
import { LinkWithDemographicsCard } from './demographics/LinkWithDemographics';
import { UsernameRecoveryCard } from './usernameRecovery/UsernameRecoveryPage';
import { CollectDemographicsCard } from './verification/demographics/CollectDemographics';
import { IdentityVerificationCard } from './verification/identityVerificationPage/IdentityVerificationPage';
import { CollectPhoneNumberCard } from './verification/phone/CollectPhoneNumberForm';
import { VerifyPhoneCodeCard } from './verification/phone/VerifyPhoneCode';
import { VerifyEntryCard } from './verification/VerifyEntry';

/**
 * [SPI-3848] The debug menu is only shown on the non-prod environments.
 * On Prod, this will become the error page (Contact Support).
 *
 * The purpose of this page is to layout all the components (vertically) so it's easy to view all components in one view.
 *
 * @param brandInfo
 * @constructor
 */
export default function DebugUXPage() {
    const { tier, identityVerification } = useContext<ClientConfig>(ClientConfigContext);
    const brandInfo = useBrand();

    const { errors, control, formState } = useForm({ mode: 'onChange' });
    const { isDirty, isValid } = formState;
    const { t } = useTranslation();

    // Only allow the debug menu to be shown on dev and local environments
    const debugTiers = ['dev', 'local'];
    if (!debugTiers.includes(tier)) {
        return <VerifyContactCustomerService />;
    }

    // For AutoInstantActivationLandingCard
    const useStyles = makeStyles(() => ({
        emailContainer: {
            alignItems: 'flex-end',
            display: 'flex',
        },
        leftButton: {
            height: 47,
            width: 144,
        },
        rightButton: {
            borderRadius: 24,
            width: 144,
        },
    }));
    const aiaLandingPageStyleClasses = useStyles();

    // For Identity Verification
    const identityVerificationPageClasses = makeStyles(() => ({
        header: {
            marginBottom: 20,
        },
    }));

    const isBulletPointsList = identityVerification.ui.list?.length > 0;

    return (
        <Grid>
            <PromptCard title={'Authentication Error'} variant='default'>
                <Typography variant='h2'>'Error message'</Typography>
            </PromptCard>
            <CustomerServiceCard closeUrl={'/'} variant='closable' />
            <ActivationCodeCard brandInfo={brandInfo} isValid={isValid} isDirty={isDirty} translation={t} errors={errors} control={control} />
            <RedirectButtonCard
                title='Provide additional information'
                content='We couldn’t find a match. Please provide additional information to help us locate your medical record.'
                url={AuthenticatedRoutes.RootRoutes.Registration}
                onNext={() => {}}
                params={{}}
            />
            <AutoInstantActivationAuthorizationCard
                control={control}
                errors={errors}
                isDirty={isDirty}
                isValid={isValid}
                handleSubmit={() => {}}
                onSubmit={() => {}}
                handleCancel={() => {}}
                t={t}
                pageState={1} // PageState.DemographicsForm = 1
            />
            <AutoInstantActivationLandingCard
                control={control}
                errors={errors}
                pageState={1} // PageState.EmailOnFile = 1
                classes={aiaLandingPageStyleClasses}
                defaultValue={'defaultEmail@email.com'}
                currentValue={'currentEmail@email.com'}
                handleNextClick={() => {}}
                handleUseDifferentEmailClick={() => {}}
                handleCancelClick={() => {}}
                t={t}
            />
            <AutoInstantActivationLandingCard
                control={control}
                errors={errors}
                pageState={2} // PageState.DifferentEmail = 2
                classes={aiaLandingPageStyleClasses}
                defaultValue={'defaultEmail@email.com'}
                currentValue={'currentEmail@email.com'}
                handleNextClick={() => {}}
                handleUseDifferentEmailClick={() => {}}
                handleCancelClick={() => {}}
                t={t}
            />
            <LinkWithDemographicsCard
                brandInfo={brandInfo}
                control={control}
                errors={errors}
                loading={false}
                callCancelUrl={() => {}}
                cancelUrl={''}
                t={t}
                isDirty={isDirty}
                isValid={isValid}
            />
            <CollectDemographicsCard
                brandInfo={brandInfo}
                control={control}
                errors={errors}
                loading={false}
                setTermsAccepted={() => {}}
                termsAccepted={false}
                phoneNumber={''}
                handleBackButtonClick={() => {}}
                handleSubmit={() => {}}
                isDirty={isDirty}
                isValid={isValid}
            />
            <CollectPhoneNumberCard
                brandInfo={brandInfo}
                control={control}
                errors={errors}
                header={'Collect Phone Number Title'}
                t={t}
                isDirty={isDirty}
                isValid={isValid}
                onCancel={() => {}}
            />
            <TransitionCard />
            <IdentityVerificationCard
                pageState={1} // PageState.NewSession
                isBulletPointsList={isBulletPointsList}
                classes={identityVerificationPageClasses}
                componentsProps={identityVerification.ui}
                updateSessionCookie={() => {}}
                token={''}
                jwt={''}
                isCrossRegion={false}
            />
            <VerifyEntryCard onActivationCodeDeny={() => {}} onActivationCodeSubmit={() => {}} />
            <VerifyPhoneCodeCard
                brandInfo={brandInfo}
                control={control}
                errors={errors}
                t={t}
                loading={false}
                phoneNumber={'123-234-4577'}
                invalid={false}
                resendVerification={() => {}}
                handleCancelClick={() => {}}
                isDirty={isDirty}
                isValid={isValid}
            />
            <UsernameRecoveryCard brandInfo={brandInfo} control={control} errors={errors} loading={false} isDirty={isDirty} isValid={isValid} t={t} />
        </Grid>
    );
}
